import React from 'react'
import {captureApplicationError, errorMonitor} from './errmon'

const D = console.log

type TStartTelemetry = () => void | (() => void)

interface IAppShellProps {
    children?: React.ReactNode
    fallback: React.ReactNode
    startTelemetry: TStartTelemetry
}

interface IAppShellState {
    error: boolean
}

/**
 * INTERCEPT unhandled exceptions (i.e. white-screen of death).
 */
export class AppShell extends React.Component<IAppShellProps, IAppShellState> {
    private prevOnError: OnErrorEventHandler | null = null
    private stopTelemetry?: () => void

    private onUnhandledRejection = (event: PromiseRejectionEvent) => {
        captureApplicationError({
            monitor: errorMonitor,
            kind: 'rejection',
            error: event.reason,
        })
    }

    constructor (props: IAppShellProps) {
        super(props)
        this.state = {error: false}
    }

    componentDidMount () {
        this.prevOnError = window.onerror
        window.onerror = (message, source, lineno, colno, error) => {
            D('ErrorBound: window.onerror:', error)
            captureApplicationError({
                monitor: errorMonitor,
                kind: 'exception',
                error: error ?? message,
                details: {source, line: lineno, column: colno},
            })
            this.setState({error: true})

            if (this.prevOnError) {
                return this.prevOnError(message, source, lineno, colno, error)
            }
            return true
        }
        window.addEventListener('unhandledrejection', this.onUnhandledRejection)
        try {
            const stopTelemetry = this.props.startTelemetry()
            if (stopTelemetry) this.stopTelemetry = stopTelemetry
        } catch (error) {
            console.error('Telemetry startup failed:', error)
        }
    }

    componentWillUnmount () {
        window.onerror = this.prevOnError
        window.removeEventListener('unhandledrejection', this.onUnhandledRejection)
        this.stopTelemetry?.()
    }

    static getDerivedStateFromError () {
        return {error: true}
    }

    componentDidCatch (error: Error, errorInfo: React.ErrorInfo) {
        captureApplicationError({
            monitor: errorMonitor,
            kind: 'render',
            error,
            details: {componentStack: errorInfo.componentStack ?? undefined},
        })
    }

    render () {
        return this.state.error ? this.props.fallback : this.props.children
    }
}
